import type { CfbVisionIntakeBatch, CfbVisionPipelineStage } from './cfbScheduleVisionTypes';

export const CFB_SCHEDULE_INTAKE_POLL_INTERVAL_MS = 4000;
export const CFB_SCHEDULE_INTAKE_MAX_POLL_ATTEMPTS = 90;

const TERMINAL_PIPELINE_STAGES = new Set<CfbVisionPipelineStage>([
  'ready_for_review',
  'failed',
]);

const IN_PROGRESS_PIPELINE_STAGES = new Set<CfbVisionPipelineStage>([
  'queued',
  'downloading',
  'analyzing',
  'correlating',
]);

export function isVodIntakeBatch(batch: Pick<CfbVisionIntakeBatch, 'intake_mode'>): boolean {
  return batch.intake_mode === 'twitch_vod';
}

export function intakeBatchPollingInProgress(
  batch: Pick<CfbVisionIntakeBatch, 'intake_mode' | 'pipeline_stage' | 'status'>
): boolean {
  if (!isVodIntakeBatch(batch)) {
    return false;
  }
  const stage = batch.pipeline_stage;
  if (stage && TERMINAL_PIPELINE_STAGES.has(stage)) {
    return false;
  }
  if (batch.status === 'failed' || batch.status === 'ready_for_review') {
    return false;
  }
  if (stage && IN_PROGRESS_PIPELINE_STAGES.has(stage)) {
    return true;
  }
  return batch.status === 'processing';
}

export function intakeBatchPollComplete(
  batch: Pick<CfbVisionIntakeBatch, 'intake_mode' | 'pipeline_stage' | 'status'>
): boolean {
  if (!isVodIntakeBatch(batch)) {
    return true;
  }
  return !intakeBatchPollingInProgress(batch);
}

export function intakeBatchPollFailed(
  batch: Pick<CfbVisionIntakeBatch, 'pipeline_stage' | 'status' | 'error_message'>
): boolean {
  return batch.pipeline_stage === 'failed' || batch.status === 'failed';
}

export function pipelineStageLabel(stage: CfbVisionPipelineStage | string | undefined): string {
  switch (stage) {
    case 'queued':
      return 'Queued for processing';
    case 'downloading':
      return 'Downloading VOD from Twitch';
    case 'analyzing':
      return 'Extracting schedule rows from video';
    case 'correlating':
      return 'Matching teams to league roster';
    case 'ready_for_review':
      return 'Ready for commissioner review';
    case 'failed':
      return 'Extraction failed';
    default:
      return 'Processing VOD intake';
  }
}

export function pipelineProgressPercent(
  batch: Pick<CfbVisionIntakeBatch, 'pipeline_progress'>
): number {
  const value = Number(batch.pipeline_progress ?? 0);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}
